import { Switch } from "@clockwise/design-system";
import { EventTable, EventTableRow } from "@clockwise/web-commons/src/components/EventTable/";
import { useGatewayQuery } from "@clockwise/web-commons/src/network/apollo/gateway-provider";
import { TrackingEvents, useTracking } from "@clockwise/web-commons/src/util/analytics.util";
import { Interval } from "luxon";
import React, { useMemo } from "react";
import { removeAutopilotEmoji } from "../../util/event.util";
import getNextOccuranceText from "../../util/getNextOccuranceText";
import { useSetFlexibility } from "../flexible-meetings/useSetFlexibility";
import { FlexibleMeetingsSuggestionsDashboardDocument } from "./graphql/__generated__/FlexibleMeetingsSuggestions.v2.generated";

const trackingSourceId = "Felxible meeting suggestions control";

export const FlexibleMeetingSuggestionsControl = () => {
  const trackEvent = useTracking();
  const { error, loading, data } = useGatewayQuery(FlexibleMeetingsSuggestionsDashboardDocument);
  const { handleFlexibilityChange, handleAllFlexibilityChange } = useSetFlexibility(
    data?.flexibleMeetingsDashboard.suggestedFlexibleMeetings ?? [],
    trackingSourceId,
  );

  const suggestions = useMemo(() => {
    const suggestions = (data?.flexibleMeetingsDashboard.suggestedFlexibleMeetings ?? [])
      .filter((meeting) => meeting.canMarkFlexible)
      .map((suggestion) => ({
        id: suggestion.externalEventId,
        attendees: suggestion.attendees.map((a) => a.person),
        flexible: !!suggestion.flexibility?.isFlexible,
        flexibilityEditable: suggestion.canMarkFlexible,
        startTime: Interval.fromISO(
          suggestion.dateOrTimeRange.__typename === "DateRange"
            ? suggestion.dateOrTimeRange.dateRange
            : suggestion.dateOrTimeRange.timeRange,
        ).start,
        recurrenceText: suggestion.timeDescription,
        title: removeAutopilotEmoji(suggestion.title) ?? "Untitled Event",
      }));
    trackEvent(TrackingEvents.FLEXIBLE_MEETING.NUM_SUGGESTIONS, {
      numSuggestions: suggestions.length,
    });
    return suggestions;
  }, [data?.flexibleMeetingsDashboard.suggestedFlexibleMeetings, trackEvent]);

  const allFlexible = suggestions.every(({ flexible }) => flexible);

  const eventTableError = error?.message ? "Unable to fetch suggestions" : undefined;

  if (!loading && !suggestions.length && !error) {
    return null; // No suggestions to show.
  }

  return (
    <>
      <div className="cw-mb-3 cw-border-b cw-border-default cw-border-solid">
        <div className="cw-flex cw-flex-wrap cw-items-center cw-mb-1">
          <div className="cw-grow">
            <h2 className="cw-heading-2xl cw-mb-0">Suggestions</h2>
            <div className="cw-body-base">
              Make these other meetings flexible to get more Focus Time in the day.
            </div>
          </div>
          <div className="cw-relative">
            <div className="cw-pb-[2px]">
              <Switch
                label={<div className="cw-body-sm">Mark all suggestions flexible</div>}
                labelPlacement="start"
                checked={allFlexible}
                disabled={loading || !suggestions.length}
                onChange={handleAllFlexibilityChange}
                cw-id="mark-all-flexible-switch"
              />
            </div>
          </div>
        </div>
      </div>
      <div>
        <EventTable
          emptyText="No suggested meetings to make flexible at this time"
          errorText={eventTableError}
          loading={loading}
          onFlexibilityChange={handleFlexibilityChange}
          showAvatars
        >
          {suggestions.map(({ attendees, id, title, startTime, recurrenceText, flexible }) => (
            <EventTableRow
              attendees={attendees}
              eventId={id}
              flexible={flexible}
              key={id}
              nextOccruance={startTime ? getNextOccuranceText(startTime) : "Unknown Date"}
              recurrence={recurrenceText}
              title={title}
            />
          ))}
        </EventTable>
      </div>
    </>
  );
};
